import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from "@microsoft/signalr";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BTCPaymentInfo, InvoiceStatus } from "../types/BTCTypes";
import moment from "moment";

interface State {
  info: BTCPaymentInfo;
  expired?: boolean;
  connection?: HubConnection;
  expiration?: moment.Duration;
  progress: number;
}

export default function useBTCPayment(
  info: BTCPaymentInfo,
  product_id: string,
  invoice_id: string
) {
  const [state, setState] = useState<State>({
    info: info,
    progress: 0,
    expiration: moment.duration(info.expiration),
    expired: false,
  });
  const connectionRef = useRef(
    new HubConnectionBuilder()
      .withUrl("/crypto")
      .configureLogging(LogLevel.Information)
      .build()
  );

  const [status, setStatus] = useState<InvoiceStatus>(
    InvoiceStatus.InvoiceCreated
  );

  const [countdownTimer, setCountdownTimer] = useState<NodeJS.Timeout>();
  const navigate = useNavigate();

  const [totalSeconds] = useState(state.expiration!.clone());

  async function checkInvoiceStatus() {
    if (
      connectionRef.current!.state != HubConnectionState.Connected ||
      state.expired
    )
      return;

    const status = await connectionRef.current.invoke<InvoiceStatus>(
      "CheckInvoiceStatus",
      product_id,
      invoice_id
    );

    switch (status) {
      case InvoiceStatus.InvoiceExpired:
      case InvoiceStatus.InvoiceInvalid: {
        setState({
          ...state,
          expired: true,
        });
        setStatus(status);
        break;
      }
      case InvoiceStatus.InvoiceProcessing: {
        setStatus(status);
        navigate("/pending", { replace: true });
        break;
      }
      case InvoiceStatus.InvoiceSettled: {
        setStatus(status);
        navigate("/success", { replace: true });
        break;
      }
    }
  }

  useEffect(() => {
    const connection = connectionRef.current;

    connection.start().catch(console.error);

    const interval = setInterval(() => {
      if (
        state &&
        (state.info.status === InvoiceStatus.InvoicePaymentSettled ||
          state.info.status === InvoiceStatus.InvoiceSettled ||
          state.info.status === InvoiceStatus.InvoiceProcessing)
      ) {
        navigate("/success", { replace: true });
      }

      if (!state.expired || state.progress < 100) {
        setState({
          ...state,
          expiration: state.expiration!.subtract("PT1S"),
        });

        const secondsEllapsed = state.expiration!.asSeconds();

        if (secondsEllapsed % 5 === 0) {
          checkInvoiceStatus().catch(console.error);
        }

        const totalSecondsNumber = totalSeconds.asSeconds();

        const totalEllapsed =
          ((totalSecondsNumber - secondsEllapsed) / totalSecondsNumber) * 100;

        setState({
          ...state,
          progress: Number.parseInt(totalEllapsed.toFixed()),
        });
      }
    }, 1000);

    setCountdownTimer(interval);

    return () => {
      clearInterval(interval);

      if (
        connection.state === HubConnectionState.Connected &&
        (state.expired ||
          status === InvoiceStatus.InvoiceExpired ||
          status === InvoiceStatus.InvoiceInvalid)
      )
        connection.stop().catch(console.error);
    };
  }, []);

  useEffect(() => {
    if (status === InvoiceStatus.InvoicePaymentSettled) {
      navigate("/success", { replace: true });
    } else if (status === InvoiceStatus.InvoiceProcessing) {
      navigate("/pending", { replace: true });
    }

    if (state.progress >= 100 || state.expired) {
      setState({
        ...state,
        expired: true,
      });
      clearInterval(countdownTimer);
      if (state.connection!.state === HubConnectionState.Connected) {
        checkInvoiceStatus()
          .then(() => {
            state.connection!.stop().catch(console.error);
          })
          .catch(console.error);
      }
    }
  }, [state.progress, state.expired, status]);

  return {
    status,
    totalSeconds,
    ...state,
  };
}
